import { createStore, del, get, keys, set } from 'idb-keyval';
import { useSyncExternalStore } from 'react';
import { ApiChyba } from '../api/client.js';

/**
 * Offline fronta terénního sběru (checklist G1–G2): mutace deníku, fotky,
 * nález a headcount pořízené bez signálu se ukládají do IndexedDB (fotky jako
 * Blob) a po připojení se automaticky odešlou v pořadí pořízení.
 *
 * VĚDOMĚ jednoduché — „offline = sběr, ne hrdinství" (ux-omezeni §2):
 *  - konflikt (409 a jiné 4xx) se NEřeší chytře: položka zůstane označená
 *    „vyřešit ručně" a sync pokračuje dalšími,
 *  - výpadek sítě / 5xx: položka zůstává, zkusí se při další synchronizaci,
 *  - do fronty (ani do žádné offline cache) NIKDY nejdou finanční data
 *    (invariant 7) — fronta nese jen terénní zápisy.
 */

/** Foto ve frontě — Blob + multipart pole (FormData se skládá až při odeslání). */
export interface FrontaSoubor {
  blob: Blob;
  nazevSouboru: string;
  mimeType: string;
  popis?: string;
}

type FrontaOperace =
  | { druh: 'json'; metoda: 'POST' | 'PATCH'; url: string; telo: unknown }
  | { druh: 'soubor'; url: string; soubor: FrontaSoubor }
  /**
   * Nález = složená operace: POST /api/diary/nalez → fotky na vzniklý Nalez.
   * Vazbu na deníkový záznam řeší SERVER podle dne založení (`telo.datum`,
   * den pořízení nálezu) — klient NIKDY neposílá id záznamu (offline snapshot
   * by nález přivěsil na poslední podepsaný den místo na dnešek).
   */
  | {
      druh: 'nalez';
      zakazkaId: string;
      telo: { datum: string; popis?: string; objektId?: string };
      soubory: FrontaSoubor[];
    };

export type FrontaPolozka = {
  id: string;
  /** Kdy byla položka pořízena (ISO) — fronta se odesílá v pořadí pořízení. */
  kdy: string;
  /** Lidský popis pro banner/menu („Deník 7. 7. — provedené práce"). */
  popis: string;
  /** Hláška serveru u konfliktu (409/4xx) — položka čeká na ruční vyřešení. */
  konflikt?: string;
} & FrontaOperace;

/** Položka před zařazením (id + čas doplní fronta). */
export type NovaFrontaPolozka = { popis: string } & FrontaOperace;

/** IndexedDB store; v prostředí bez IndexedDB (testy, stará WebView) Map v paměti. */
const idbDostupne = typeof indexedDB !== 'undefined';
const store = idbDostupne ? createStore('helfr-offline', 'fronta') : undefined;
const pametovaFronta = new Map<string, FrontaPolozka>();

async function ulozPolozku(p: FrontaPolozka): Promise<void> {
  if (store) await set(p.id, p, store);
  else pametovaFronta.set(p.id, p);
}

async function smazPolozku(id: string): Promise<void> {
  if (store) await del(id, store);
  else pametovaFronta.delete(id);
}

async function nactiVse(): Promise<FrontaPolozka[]> {
  let polozky: FrontaPolozka[];
  if (store) {
    const ids = await keys(store);
    polozky = (await Promise.all(ids.map((k) => get<FrontaPolozka>(k, store)))).filter(
      (p): p is FrontaPolozka => !!p,
    );
  } else {
    polozky = [...pametovaFronta.values()];
  }
  return polozky.sort((a, b) => a.kdy.localeCompare(b.kdy));
}

// --- Stav pro UI (useSyncExternalStore) ---

export interface FrontaStav {
  /** Počet položek čekajících na odeslání (bez konfliktů). */
  ceka: number;
  /** Položky odmítnuté serverem — vyřešit ručně. */
  konflikty: FrontaPolozka[];
  online: boolean;
  synchronizuji: boolean;
}

let stav: FrontaStav = { ceka: 0, konflikty: [], online: true, synchronizuji: false };
const posluchaci = new Set<() => void>();

function ohlas(zmena: Partial<FrontaStav>): void {
  stav = { ...stav, ...zmena };
  for (const p of posluchaci) p();
}

async function prepocitej(): Promise<void> {
  const vse = await nactiVse();
  ohlas({
    ceka: vse.filter((p) => !p.konflikt).length,
    konflikty: vse.filter((p) => !!p.konflikt),
  });
}

export function jeOnline(): boolean {
  return typeof navigator === 'undefined' || navigator.onLine !== false;
}

/** Po úspěšné synchronizaci (aspoň 1 položka odeslána) — refresh dotazů v UI. */
let poSynchronizaci: (() => void) | null = null;
export function nastavPoSynchronizaci(fn: (() => void) | null): void {
  poSynchronizaci = fn;
}

// --- Zařazení do fronty ---

export async function pridejDoFronty(polozka: NovaFrontaPolozka): Promise<void> {
  const id =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  await ulozPolozku({ ...polozka, id, kdy: new Date().toISOString() });
  await prepocitej();
}

/** Ruční odebrání konfliktní položky (jediné „řešení" — data si mistr přepíše). */
export async function odeberZFronty(id: string): Promise<void> {
  await smazPolozku(id);
  await prepocitej();
}

// --- Odeslání ---

function slozFormData(s: FrontaSoubor): FormData {
  const form = new FormData();
  form.set('soubor', s.blob, s.nazevSouboru);
  form.set('nazevSouboru', s.nazevSouboru);
  form.set('mimeType', s.mimeType);
  if (s.popis) form.set('popis', s.popis);
  return form;
}

async function fetchJson(url: string, init: RequestInit): Promise<unknown> {
  const res = await fetch(url, { credentials: 'include', ...init });
  let telo: unknown = null;
  try {
    telo = await res.json();
  } catch {
    telo = null;
  }
  if (!res.ok) {
    const error =
      telo !== null && typeof telo === 'object' && 'error' in telo
        ? String((telo as { error: unknown }).error)
        : `Chyba serveru (HTTP ${res.status}).`;
    throw new ApiChyba(res.status, error, telo);
  }
  return telo;
}

/** Odešle jednu položku fronty. Vyhazuje ApiChybu / síťovou chybu volajícímu. */
async function odesliPolozku(p: FrontaPolozka): Promise<void> {
  if (p.druh === 'json') {
    await fetchJson(p.url, {
      method: p.metoda,
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(p.telo),
    });
    return;
  }
  if (p.druh === 'soubor') {
    await fetchJson(p.url, { method: 'POST', body: slozFormData(p.soubor) });
    return;
  }
  // Nález: založit Nalez, pak na něj pověsit fotky (endpoint /nalezy/:id/prilohy).
  const nalez = (await fetchJson('/api/diary/nalez', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ zakazkaId: p.zakazkaId, ...p.telo }),
  })) as { id: string };
  for (const s of p.soubory) {
    await fetchJson(`/api/diary/nalezy/${nalez.id}/prilohy`, {
      method: 'POST',
      body: slozFormData(s),
    });
  }
}

let synchronizace: Promise<void> | null = null;

/**
 * Projede frontu v pořadí pořízení a odešle, co jde:
 *  - úspěch → položka pryč,
 *  - 4xx (typicky 409 – podepsaný den) → konflikt, „vyřešit ručně", jede se dál,
 *  - síťová chyba / 5xx → stop, zkusí se příště (auto při události `online`).
 */
export function synchronizuj(): Promise<void> {
  synchronizace ??= (async () => {
    ohlas({ synchronizuji: true, online: jeOnline() });
    try {
      const vse = await nactiVse();
      let odeslano = false;
      for (const p of vse) {
        if (p.konflikt) continue;
        try {
          await odesliPolozku(p);
          await smazPolozku(p.id);
          odeslano = true;
        } catch (err) {
          if (err instanceof ApiChyba && err.status >= 400 && err.status < 500) {
            await ulozPolozku({ ...p, konflikt: err.message });
            continue;
          }
          break; // síť / 5xx — příště
        }
      }
      if (odeslano) poSynchronizaci?.();
    } finally {
      await prepocitej();
      ohlas({ synchronizuji: false });
      synchronizace = null;
    }
  })();
  return synchronizace;
}

/**
 * Odešle terénní mutaci, a pokud jsme offline (nebo spojení selže), zařadí ji
 * do fronty. Vrací true = odesláno hned, false = čeká ve frontě.
 * Chyby serveru (4xx/5xx při online) propadají volajícímu — to není offline.
 */
export async function odesliNeboZarad(polozka: NovaFrontaPolozka): Promise<boolean> {
  if (!jeOnline()) {
    await pridejDoFronty(polozka);
    return false;
  }
  try {
    await odesliPolozku({ ...polozka, id: '', kdy: '' });
    return true;
  } catch (err) {
    if (err instanceof ApiChyba) throw err; // server odpověděl — řeší UI
    await pridejDoFronty(polozka); // síť spadla uprostřed → do fronty
    return false;
  }
}

// --- Auto-sync + React hook ---

let posloucham = false;
function zapniPoslech(): void {
  if (posloucham || typeof window === 'undefined') return;
  posloucham = true;
  window.addEventListener('online', () => {
    ohlas({ online: true });
    void synchronizuj();
  });
  window.addEventListener('offline', () => ohlas({ online: false }));
  // Opakovaný pokus (retry) — levná pojistka, když událost `online` nepřijde.
  setInterval(() => {
    if (jeOnline() && stav.ceka > 0 && !stav.synchronizuji) void synchronizuj();
  }, 30_000);
  ohlas({ online: jeOnline() });
  void prepocitej();
}

export function useOfflineFronta(): FrontaStav {
  zapniPoslech();
  return useSyncExternalStore(
    (cb) => {
      posluchaci.add(cb);
      return () => posluchaci.delete(cb);
    },
    () => stav,
    () => stav,
  );
}

/** Jen pro testy — vyprázdní frontu a resetuje stav. */
export async function _vyprazdniFrontuProTesty(): Promise<void> {
  for (const p of await nactiVse()) await smazPolozku(p.id);
  await prepocitej();
}

/** Jen pro testy — aktuální obsah fronty. */
export function _frontaProTesty(): Promise<FrontaPolozka[]> {
  return nactiVse();
}
